import { useEffect, useState } from "react";
import {
  MapPin,
  Navigation,
  Phone,
  Clock3,
  ArrowRight,
  CheckCircle2,
  Route,
  LocateFixed,
  Calendar,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import "./Directions.css";

const DESTINATION = "20308 Logan Ave, Langley, BC V3A 4L7";

function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" },
    );

    const elements = document.querySelectorAll(
      ".reveal, .reveal-left, .reveal-right, .reveal-scale",
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}

function openDirections(start: string) {
  const encodedStart = encodeURIComponent(start);
  const encodedDest = encodeURIComponent(DESTINATION);

  window.open(
    `https://www.google.com/maps/dir/${encodedStart}/${encodedDest}`,
    "_blank",
    "noopener,noreferrer",
  );
}

function openMapLocation() {
  const encodedDest = encodeURIComponent(DESTINATION);

  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodedDest}`,
    "_blank",
    "noopener,noreferrer",
  );
}

function HeroSection() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section className="directions-hero">
      <div className="directions-hero-bg">
        <div
          className="directions-hero-grid"
          style={{
            transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
          }}
        />
        <div className="directions-hero-orb directions-hero-orb-red" />
        <div className="directions-hero-orb directions-hero-orb-gold" />
        <div className="road-pattern directions-hero-road" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="directions-hero-content text-center">
          <div className="directions-hero-badge animate-slide-up">
            <Navigation className="w-4 h-4" />
            <span>Find Your Way</span>
          </div>

          <h1
            className="directions-hero-title animate-slide-up"
            style={{ animationDelay: "0.1s" }}
          >
            <span className="text-white">FIND YOUR </span>
            <span className="hero-gradient-text">WAY</span>
            <span className="text-white"> TO US</span>
          </h1>

          <p
            className="directions-hero-subtitle animate-slide-up"
            style={{ animationDelay: "0.2s" }}
          >
            Visit Big Boys Motor Club showroom in Langley, BC. Get directions to
            explore our premium pre-owned vehicle collection.
          </p>

          <div
            className="directions-hero-location-wrap animate-slide-up"
            style={{ animationDelay: "0.3s" }}
          >
            <div className="directions-hero-location glass-dark">
              <div className="directions-hero-location-head">
                <div className="directions-hero-location-icon">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <span className="directions-hero-location-label">
                  Our Location
                </span>
              </div>

              <p className="directions-hero-location-title">20308 Logan Ave</p>
              <p className="directions-hero-location-text">
                Langley, BC V3A 4L7
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DirectionsRouteSection() {
  const [startLocation, setStartLocation] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const highlights = [
    "Easy highway access from Trans-Canada Highway",
    "Free parking available on-site",
    "Conveniently located in central Langley for easy city access",
  ];

  const quickDirections = ["Vancouver", "Surrey", "Burnaby", "Richmond"];

  const handleGetDirections = () => {
    if (!startLocation.trim()) return;

    setIsLoading(true);
    openDirections(startLocation);

    window.setTimeout(() => {
      setIsLoading(false);
    }, 900);
  };

  const handleQuickDirection = (city: string) => {
    setStartLocation(city);
    openDirections(city);
  };

  return (
    <section
      className="relative overflow-hidden bg-[#f7f6f4]"
      style={{ marginTop: "68px" }}
    >
      <div className="directions-soft-orb directions-soft-orb-left" />
      <div className="directions-soft-orb directions-soft-orb-right" />

      <div className="container mx-auto px-4 py-16 lg:py-20 relative z-10">
        <div className="grid lg:grid-cols-[1.02fr_1fr] gap-12 xl:gap-16 items-center">
          <div className="reveal-left">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#f5d7d7] text-[#e22b1d] px-4 py-2 font-semibold text-sm shadow-sm">
              <Route className="w-5 h-5" />
              <span>Plan Your Route</span>
            </div>

            <h1 className="mt-6 font-sans text-[38px] sm:text-[48px] lg:text-[56px] leading-[1] font-black tracking-[-0.04em] text-[#0b1534]">
              Get <span className="text-[#ea2b22]">Directions</span>
            </h1>

            <p className="mt-6 max-w-[720px] text-[17px] sm:text-[18px] leading-[1.75] text-[#334e73] font-medium">
              Enter your starting location and we&apos;ll guide you to our
              showroom. Our dealership is conveniently located in Langley with
              easy access from major highways.
            </p>

            <div className="mt-8 space-y-5">
              {highlights.map((item) => (
                <div key={item} className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#d9f0df] shrink-0">
                    <CheckCircle2 className="h-6 w-6 text-[#1fa34a]" />
                  </div>
                  <p className="text-[16px] sm:text-[17px] text-[#163256] font-medium">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal-right">
            <Card className="direction-card border-0 overflow-hidden bg-white shadow-none rounded-[30px]">
              <div className="direction-card-topbar" />
              <CardContent className="p-6 md:p-8 lg:p-10">
                <div className="space-y-7">
                  <div>
                    <label className="block text-[17px] font-semibold text-[#163256] mb-4">
                      Destination
                    </label>

                    <div className="direction-input-shell">
                      <div className="direction-input-icon bg-[#f8e7e7] text-[#f07b72]">
                        <MapPin className="w-5 h-5" />
                      </div>

                      <Input
                        value={DESTINATION}
                        disabled
                        className="direction-input-field bg-transparent border-0 shadow-none text-[#9aa3b2] font-semibold"
                      />
                    </div>
                  </div>

                  <div className="flex justify-center -my-1">
                    <div className="direction-route-badge">
                      <Route className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[17px] font-semibold text-[#163256] mb-4">
                      Starting Location
                    </label>

                    <div className="direction-input-shell">
                      <div className="direction-input-icon bg-[#f4e5b0] text-[#e28a00]">
                        <LocateFixed className="w-5 h-5" />
                      </div>

                      <Input
                        placeholder="Enter your address or city..."
                        value={startLocation}
                        onChange={(e) => setStartLocation(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleGetDirections();
                        }}
                        className="direction-input-field bg-transparent border-0 shadow-none text-[#5e6f88] placeholder:text-[#7c8597]"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleGetDirections}
                    disabled={!startLocation.trim() || isLoading}
                    className={`direction-main-btn w-full rounded-[20px] py-7 text-[20px] font-extrabold ${
                      startLocation.trim()
                        ? "direction-main-btn--enabled"
                        : "direction-main-btn--disabled"
                    }`}
                  >
                    {isLoading ? (
                      "Getting Directions..."
                    ) : (
                      <>
                        <Navigation className="w-5 h-5 mr-3" />
                        Get Directions
                        <ArrowRight className="w-5 h-5 ml-3" />
                      </>
                    )}
                  </Button>

                  <div className="border-t border-[#e8edf5] pt-7">
                    <p className="text-[16px] text-[#56708f] mb-5">
                      Quick directions from:
                    </p>

                    <div className="flex flex-wrap gap-3">
                      {quickDirections.map((city) => (
                        <button
                          key={city}
                          type="button"
                          onClick={() => handleQuickDirection(city)}
                          className="direction-chip"
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}

function MapShowcaseSection() {
  return (
    <section className="map-showcase-section relative overflow-hidden">
      <div className="map-glow map-glow-left" />
      <div className="map-glow map-glow-center" />

      <div className="container mx-auto px-4 py-16 lg:py-20 relative z-10">
        <div className="max-w-5xl mx-auto text-center reveal">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#a92626]/60 bg-[rgba(103,23,34,0.35)] px-4 py-2 text-[#ff675d] font-semibold text-sm">
            <MapPin className="w-5 h-5" />
            <span>Our Location</span>
          </div>

          <h2 className="mt-6 font-sans text-[38px] sm:text-[48px] lg:text-[56px] leading-[1.05] font-black tracking-[-0.04em] text-white">
            Find Us on the <span className="map-gradient-text">Map</span>
          </h2>

          <p className="mt-4 max-w-3xl mx-auto text-[17px] sm:text-[18px] leading-[1.75] text-[#9db2d1]">
            Conveniently located in Langley, our showroom is easily accessible
            from all major routes in the Lower Mainland.
          </p>
        </div>

        <div className="mt-10 reveal-scale">
          <div className="premium-map-frame">
            <div className="premium-map-actions">
              <button
                type="button"
                onClick={openMapLocation}
                className="premium-map-link"
              >
                Open in Google Maps
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>

            <div className="premium-map-wrap">
              <iframe
                src="https://maps.google.com/maps?q=20308%20Logan%20Ave%2C%20Langley%2C%20BC%20V3A%204L7&z=15&output=embed"
                title="Big Boys Motor Club Location"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0"
              />
            </div>

            <div className="premium-map-address-card">
              <div className="premium-map-address-icon">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.18em] text-[#9cb0cf] font-semibold">
                  Showroom Address
                </p>
                <p className="mt-1 text-white text-lg font-bold">
                  20308 Logan Ave, Langley, BC V3A 4L7
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function VisitInfoSection() {
  const hours = [
    { label: "Monday - Friday", value: "9:00 AM - 8:00 PM" },
    { label: "Saturday", value: "9:00 AM - 6:00 PM" },
    { label: "Sunday", value: "11:00 AM - 5:00 PM" },
  ];

  const contacts = [
    { label: "Sales", value: "(604) 358-8081" },
    { label: "Service", value: "(604) 358-8082" },
    { label: "Email", value: "info@bigboysmotorclub.com" },
  ];

  return (
    <section className="visit-info-section relative overflow-hidden bg-[#f7f6f4]">
      <div className="visit-orb visit-orb-left" />
      <div className="visit-orb visit-orb-right" />

      <div className="container mx-auto px-4 py-16 lg:py-20 relative z-10">
        <div className="max-w-5xl mx-auto text-center reveal">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#f4e3a9] px-4 py-2 text-[#d97706] font-semibold text-sm shadow-sm">
            <Calendar className="w-5 h-5" />
            <span>Visit Us</span>
          </div>

          <h2 className="mt-6 font-sans text-[36px] sm:text-[46px] lg:text-[56px] leading-[1.06] font-black tracking-[-0.04em] text-[#091533]">
            Everything You Need to <span className="text-[#ea2b22]">Know</span>
          </h2>

          <p className="mt-4 text-[17px] sm:text-[18px] leading-[1.75] text-[#425b7a] max-w-3xl mx-auto">
            Plan your visit with all the information you need for a seamless
            experience.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <Card className="visit-card reveal-left border-0 rounded-[30px] bg-white shadow-none">
            <CardContent className="p-8">
              <div className="visit-card-icon bg-[#dbe8ff] text-[#4b82ea]">
                <Clock3 className="w-8 h-8" />
              </div>

              <h3 className="visit-card-title">Business Hours</h3>

              <div className="mt-10 space-y-6">
                {hours.map((item) => (
                  <div key={item.label} className="visit-row">
                    <span className="visit-row-label">{item.label}</span>
                    <span className="visit-row-value text-[#091533]">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="visit-card reveal-right border-0 rounded-[30px] bg-white shadow-none">
            <CardContent className="p-8">
              <div className="visit-card-icon bg-[#d9f0df] text-[#22c55e]">
                <Phone className="w-8 h-8" />
              </div>

              <h3 className="visit-card-title">Contact Us</h3>

              <div className="mt-10 space-y-6">
                {contacts.map((item) => (
                  <div key={item.label} className="visit-row">
                    <span className="visit-row-label">{item.label}</span>
                    <span className="visit-row-value text-[#38d46e]">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function DirectionsPage() {
  useScrollReveal();

  return (
    <main className="min-h-screen bg-white">
      <HeroSection />
      <DirectionsRouteSection />
      <MapShowcaseSection />
      <VisitInfoSection />
    </main>
  );
}

export default DirectionsPage;
